import { useCallback, useState } from 'react'
import {
  RASPADINHA_PUBLIC_COOKIE_POLICY_DOC_ID,
  RASPADINHA_PUBLIC_REGULATION_DOC_ID,
} from '@/lib/raspadinha-public'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'
import { RaspadinhaPublicDocumentsModal } from './RaspadinhaPublicDocumentsModal'
import styles from './RaspadinhaPublicWelcomeStep.module.css'

type RaspadinhaPublicWelcomeStepProps = {
  campaign: RaspadinhaPublicCampaign
  onContinue: () => void
}

export function RaspadinhaPublicWelcomeStep({
  campaign,
  onContinue,
}: RaspadinhaPublicWelcomeStepProps) {
  const imageAltLabel = campaign.title.trim() || campaign.name.trim() || 'Campanha raspadinha'
  const hasWelcomeImage = campaign.welcome_image_url.trim().length > 0
  const regulationDocuments = campaign.regulation_documents
  const hasRegulationDocuments = regulationDocuments.length > 0

  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false)
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<string | null>(null)

  const openDocumentsModal = useCallback(
    (preferredDocumentId: string) => {
      if (!hasRegulationDocuments) {
        return
      }

      const resolvedDocumentId = regulationDocuments.some(
        (document) => document.id === preferredDocumentId
      )
        ? preferredDocumentId
        : regulationDocuments[0]?.id ?? null

      setHighlightedDocumentId(resolvedDocumentId)
      setIsDocumentsModalOpen(true)
    },
    [hasRegulationDocuments, regulationDocuments]
  )

  const closeDocumentsModal = useCallback(() => {
    setIsDocumentsModalOpen(false)
    setHighlightedDocumentId(null)
  }, [])

  return (
    <>
      <section className={styles.welcome} aria-label="Boas-vindas da campanha raspadinha">
        {hasWelcomeImage ? (
          <div className={styles.imageWrap}>
            <img
              className={styles.welcomeImage}
              src={campaign.welcome_image_url}
              alt={`Boas-vindas — ${imageAltLabel}`}
              width={1000}
              height={1000}
              loading="eager"
              decoding="async"
            />
          </div>
        ) : (
          <div className={styles.imagePlaceholder} aria-hidden>
            <span className={styles.imagePlaceholderLabel}>Imagem de boas-vindas</span>
          </div>
        )}

        <div className={styles.content}>
          <p className={styles.legalNotice}>
            Ao clicar em continuar, você aceita e concorda com o{' '}
            {hasRegulationDocuments ? (
              <button
                type="button"
                className={styles.legalLink}
                onClick={() => openDocumentsModal(RASPADINHA_PUBLIC_REGULATION_DOC_ID)}
              >
                regulamento
              </button>
            ) : (
              'regulamento'
            )}{' '}
            e{' '}
            {hasRegulationDocuments ? (
              <button
                type="button"
                className={styles.legalLink}
                onClick={() => openDocumentsModal(RASPADINHA_PUBLIC_COOKIE_POLICY_DOC_ID)}
              >
                políticas de cookies
              </button>
            ) : (
              'políticas de cookies'
            )}
            .
          </p>

          <button type="button" className={styles.continueButton} onClick={onContinue}>
            Continuar
          </button>
        </div>
      </section>

      {isDocumentsModalOpen ? (
        <RaspadinhaPublicDocumentsModal
          documents={regulationDocuments}
          highlightedDocumentId={highlightedDocumentId}
          onClose={closeDocumentsModal}
        />
      ) : null}
    </>
  )
}
